import { maRutGon, nhanDonVi } from './don-vi.ts';
import { sanhTheo, type ChonDuoc, type SoSanhChon } from './tuy-chon-loc.ts';
import type { NhomPhanRa } from './types-thong-ke.ts';
import type { IdKemNhan, TrangThaiGia, TruyVanLuotGoi, TruyVanTrace, TuyChonLocAi } from './types-vet.ts';

/**
 * Bộ lọc dùng chung của màn Sử dụng AI (mọi tab) và của số liệu AI ở Tổng quan, và cách nó thành tham số của TỪNG
 * endpoint — port `loc-ai.ts` native.
 *
 * ── MỘT TRẠNG THÁI, MỖI TAB MỘT TẬP Ô ────────────────────────────────────
 * Mỗi endpoint nhận một tập tham số khác nhau (native `vet.py`, platform `module/VetController.java` giữ y):
 * `/vet/luot-goi` và `/vet/thong-ke-ai` nhận đủ tám, `/vet/trace` chỉ nhận `service` và `actor_id`, `/vet/luong` không
 * nhận ô nào trong số này. Tab nào chỉ HIỆN ô mà endpoint của nó thật sự áp — một ô hiện mà không lọc là một ô nói dối.
 */

export const TRUONG_LOC_AI = [
  'moHinh',
  'nhaCungCap',
  'service',
  'tinhNang',
  'nguoiDungId',
  'donViId',
  'trangThai',
  'coCanhBao',
] as const;

export type TruongLocAi = (typeof TRUONG_LOC_AI)[number];

export interface LocAi {
  moHinh?: string;
  nhaCungCap?: string;
  service?: string;
  /** MÃ tính năng (snake_case), không phải nhãn — audit-service lọc theo cột `tinh_nang`. */
  tinhNang?: string;
  nguoiDungId?: string;
  donViId?: string;
  trangThai?: TrangThaiGia;
  coCanhBao?: boolean;
}

export const LOC_AI_MAC_DINH: LocAi = {};

export const TAB_SU_DUNG_AI = ['tong-quan', 'trace', 'luot-goi', 'luong'] as const;

export type TabSuDungAi = (typeof TAB_SU_DUNG_AI)[number];

/** Ô lọc mà endpoint của từng tab thật sự áp. */
export const TRUONG_THEO_TAB: Record<TabSuDungAi, readonly TruongLocAi[]> = {
  'tong-quan': TRUONG_LOC_AI,
  trace: ['service', 'nguoiDungId'],
  'luot-goi': TRUONG_LOC_AI,
  luong: [],
};

type ThamSoLocLuot = Pick<
  TruyVanLuotGoi,
  'mo_hinh' | 'nha_cung_cap' | 'service' | 'tinh_nang' | 'nguoi_dung_id' | 'don_vi_id' | 'trang_thai' | 'co_canh_bao'
>;

/** Tham số lọc của `/vet/luot-goi` và `/vet/thong-ke-ai` — chỉ khoá có giá trị, để `queryKey` ổn định. */
export function thamSoLocLuot(loc: LocAi): ThamSoLocLuot {
  return {
    ...(loc.moHinh && { mo_hinh: loc.moHinh }),
    ...(loc.nhaCungCap && { nha_cung_cap: loc.nhaCungCap }),
    ...(loc.service && { service: loc.service }),
    ...(loc.tinhNang && { tinh_nang: loc.tinhNang }),
    ...(loc.nguoiDungId && { nguoi_dung_id: loc.nguoiDungId }),
    ...(loc.donViId && { don_vi_id: loc.donViId }),
    ...(loc.trangThai && { trang_thai: loc.trangThai }),
    ...(loc.coCanhBao !== undefined && { co_canh_bao: loc.coCanhBao }),
  };
}

/**
 * Tham số lọc của `/vet/trace`. `nguoiDungId` thành `actor_id`: hai cột cùng một id — `chi_phi_llm.nguoi_dung_id` được
 * ghi từ `actor_id` của chính sự kiện.
 */
export function thamSoLocTrace(loc: LocAi): Pick<TruyVanTrace, 'service' | 'actor_id'> {
  return {
    ...(loc.service && { service: loc.service }),
    ...(loc.nguoiDungId && { actor_id: loc.nguoiDungId }),
  };
}

/** Có ô lọc nào trong `truong` đang đặt không — để mời "Xoá bộ lọc". */
export function coLocAi(loc: LocAi, truong: readonly TruongLocAi[]): boolean {
  return truong.some((k) => loc[k] !== undefined && loc[k] !== '');
}

export interface TuyChonAiManHinh {
  moHinh: ChonDuoc[];
  nhaCungCap: ChonDuoc[];
  service: ChonDuoc[];
  tinhNang: ChonDuoc[];
  nguoi: ChonDuoc[];
  donVi: ChonDuoc[];
  biCat: boolean;
}

function ma(ds: readonly string[], sanh: SoSanhChon): ChonDuoc[] {
  return ds.map((value) => ({ value, label: value })).sort(sanh);
}

/** Một id đổi tên ra hai dòng (tên là bản chụp) — cùng `value`, khác `key`. */
function kemNhan(ds: readonly IdKemNhan[], nhan: (d: IdKemNhan) => string, sanh: SoSanhChon): ChonDuoc[] {
  return ds.map((d, i) => ({ value: d.id, label: nhan(d), key: `${d.id}#${i}` })).sort(sanh);
}

/**
 * Danh mục `/vet/bo-loc` → tuỳ chọn của các ô. Danh mục chưa về thì các ô rỗng nhưng vẫn dùng được với giá trị đang
 * đặt.
 */
export function tuyChonLocAi(
  boLoc: TuyChonLocAi | undefined,
  danhBaDonVi: ReadonlyMap<string, string>,
  locale = 'vi',
): TuyChonAiManHinh {
  if (!boLoc) {
    return { moHinh: [], nhaCungCap: [], service: [], tinhNang: [], nguoi: [], donVi: [], biCat: false };
  }
  const sanh = sanhTheo(locale);
  return {
    moHinh: ma(boLoc.mo_hinh, sanh),
    nhaCungCap: ma(boLoc.nha_cung_cap, sanh),
    service: ma(boLoc.service, sanh),
    tinhNang: kemNhan(
      boLoc.tinh_nang.map((t) => ({ id: t.ma, nhan: t.nhan })),
      (d) => d.nhan ?? d.id,
      sanh,
    ),
    nguoi: kemNhan(boLoc.nguoi, (d) => d.nhan ?? maRutGon(d.id), sanh),
    donVi: kemNhan(boLoc.don_vi, (d) => nhanDonVi(d.id, danhBaDonVi, d.nhan), sanh),
    biCat: boLoc.bi_cat === true,
  };
}

/**
 * Nhãn của một dòng bảng phân rã. `/vet/thong-ke-ai` gom theo CỘT THÔ, nên ở ba nhóm đơn vị / người dùng / tính năng
 * `khoa` là id hay mã — tra lại tên ở danh mục lọc; không có thì giữ `khoa`.
 */
export function nhanKhoaPhanRa(chon: TuyChonAiManHinh, nhom: NhomPhanRa, khoa: string): string {
  const ds =
    nhom === 'theo_don_vi'
      ? chon.donVi
      : nhom === 'theo_nguoi_dung'
        ? chon.nguoi
        : nhom === 'theo_tinh_nang'
          ? chon.tinhNang
          : null;
  return ds?.find((c) => c.value === khoa)?.label ?? khoa;
}
